import React from 'react';
import { HumanTask, NEW_USER_DEADLINE_BOOST_MINUTES } from '@dreamai/types';

export type RequesterTask = HumanTask;

export { NEW_USER_DEADLINE_BOOST_MINUTES };

export type RequesterKind = 'priority_user' | 'new_user' | null;

/** Priority user wins over new user when both apply. */
export const requesterKind = (task: RequesterTask): RequesterKind => {
  if (task.requesterIsPriorityUser) return 'priority_user';
  if (task.requesterIsNewUser) return 'new_user';
  return null;
};

/** Extra class for the task tile, or '' when the requester is ordinary. */
export const requesterTileClass = (task: RequesterTask): string => {
  const kind = requesterKind(task);
  return kind ? `task-row--${kind.replace('_', '-')}` : '';
};

/**
 * Deadline used for on-screen ordering: priority-user tasks come first,
 * new-user tasks are pulled forward by the boost, tasks without a deadline
 * go last. Same shape as the backend's effective deadline so the list order
 * matches what "Pick Next Task" will hand out.
 */
export const effectiveDeadlineMs = (task: RequesterTask): number | null => {
  if (!task.deadline) return null;
  const ms = new Date(task.deadline).getTime();
  return task.requesterIsNewUser
    ? ms - NEW_USER_DEADLINE_BOOST_MINUTES * 60 * 1000
    : ms;
};

export const compareTasksForDisplay = (
  a: RequesterTask,
  b: RequesterTask,
): number => {
  const aPriority = a.requesterIsPriorityUser ? 1 : 0;
  const bPriority = b.requesterIsPriorityUser ? 1 : 0;
  if (aPriority !== bPriority) return bPriority - aPriority;

  const aDeadline = effectiveDeadlineMs(a);
  const bDeadline = effectiveDeadlineMs(b);
  if (aDeadline === null && bDeadline === null) return 0;
  if (aDeadline === null) return 1;
  if (bDeadline === null) return -1;
  return aDeadline - bDeadline;
};

const BADGE: Record<
  Exclude<RequesterKind, null>,
  { label: string; title: string }
> = {
  priority_user: {
    label: 'Priority user',
    title: 'Investor / VC — top priority, admin only',
  },
  new_user: {
    label: 'New user',
    title: 'New user — first delivered intents drive retention',
  },
};

/** Text badge (never colour alone) for who requested the task. */
export const RequesterBadge = ({ task }: { task: RequesterTask }) => {
  const kind = requesterKind(task);
  if (!kind) return null;
  const { label, title } = BADGE[kind];
  return (
    <span
      className={`task-requester-badge task-requester-badge--${kind.replace('_', '-')}`}
      title={title}
    >
      {label}
    </span>
  );
};
